import { useState } from "react";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { toast } from "sonner";

export function ImportExport() {
  const { state, setState } = useStore();
  const [text, setText] = useState("");

  function downloadJson() {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guild-masters-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function copyJson() {
    navigator.clipboard.writeText(JSON.stringify(state, null, 2));
    toast.success("Copied to clipboard");
  }

  function importJson() {
    try {
      const parsed = JSON.parse(text);
      if (!parsed.cards || !parsed.referenceData) throw new Error("missing fields");
      if (!confirm("Replace ALL current state with imported data?")) return;
      setState(() => parsed);
      toast.success("State imported");
      setText("");
    } catch (e: any) {
      toast.error(`Import failed: ${e.message}`);
    }
  }

  function importMerge() {
    try {
      const parsed = JSON.parse(text);
      setState((s) => ({
        cards: mergeById(s.cards, parsed.cards ?? []),
        decks: mergeById(s.decks, parsed.decks ?? []),
        referenceData: parsed.referenceData ?? s.referenceData,
      }));
      toast.success("Merged");
      setText("");
    } catch (e: any) {
      toast.error(`Merge failed: ${e.message}`);
    }
  }

  return (
    <div className="space-y-4">
      <PageTitle>Import / Export</PageTitle>
      <Panel>
        <div className="flex flex-wrap gap-2">
          <Button onClick={downloadJson}>Download JSON</Button>
          <Button variant="secondary" onClick={copyJson}>Copy JSON</Button>
        </div>
        <p className="mt-2 text-xs text-[#64748b]">
          State: {state.cards.length} cards · {state.decks.length} decks · {state.referenceData.glossary.length} glossary entries
        </p>
      </Panel>

      <Panel className="space-y-2">
        <h3 className="font-display text-sm font-semibold">Import</h3>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste exported JSON here…" />
        <div className="flex gap-2">
          <Button onClick={importMerge}>Merge</Button>
          <Button variant="danger" onClick={importJson}>Replace all</Button>
        </div>
      </Panel>
    </div>
  );
}

function mergeById<T extends { id: string }>(base: T[], add: T[]): T[] {
  const m = new Map(base.map((x) => [x.id, x]));
  add.forEach((x) => m.set(x.id, x));
  return Array.from(m.values());
}
